/* eslint-disable jsx-a11y/click-events-have-key-events */
/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable react/no-array-index-key */
/* eslint-disable react/prop-types */
import { useCallback } from 'react';
import makeStyles from '@material-ui/core/styles/makeStyles';
import { Box, Chip, darken, Fade, lighten, LinearProgress, Typography } from '@material-ui/core';
import clsx from 'clsx';
import { ExpandMore } from '@material-ui/icons';
import { t, Trans } from '@lingui/macro';
import { i18n } from '@lingui/core';
import { isEmpty, map, noop, uniq } from 'lodash';

import StatusBlock from '../../status-block/StatusBlock';
import { getUrlParamFromEncodedState } from '../../../utilities/FilterStateUrlUtilities';
import { siteFilterId, siteGroupFilterId, timeRangeFilterId } from '../../filters';
import { useSiteStatusTrend } from './UseSiteData';
import { useStatusColors } from '../../../utilities/ColorUtilities';
import { useFilterState } from '../../../stores/FilterStateStore';
import { usePageFilterState } from '../../page-framework/panel/Panel';
import { AND } from '../../filters/tag-filter/TagFilterStateService';
import { useIsSiteExpanded, useSidebarStateActions } from './SidebarStateProvider';
import { getWorstStatusColor } from '../../../utilities/StatusUtilities';
import ProblemsList from './ProblemsList';
import { usePageState } from '../../../stores/PageStateStore';
import { getUrlParamFromState } from '../../../utilities/PageStateUrlUtilities';
import { DrillInLink } from '../../links/DrillInLink';

const siteDetailPageId = 'site-detail';
const selectedTimeId = 'SelectedTime';

const useStyles = makeStyles((theme) => ({
    root: {
        position: 'relative',
        marginTop: theme.spacing(1),
        padding: theme.spacing(1, 2),

        transition: 'background 0.2s ease',

        background:
            theme.palette.type === 'light'
                ? darken(theme.palette.background.paper, 0.02)
                : lighten(theme.palette.background.paper, 0.02),

        '&:first-child': {
            marginTop: 0,
        },

        '&:hover': {
            background:
                theme.palette.type === 'light'
                    ? darken(theme.palette.background.paper, 0.03)
                    : lighten(theme.palette.background.paper, 0.06),
        },
    },

    row: {
        ...theme.mixins.flex.displayFlex,
        ...theme.mixins.flex.flexDirectionRow,
        ...theme.mixins.flex.alignItemsCenter,

        cursor: 'default',
        marginTop: theme.spacing(0.5),

        '&:first-child': {
            marginTop: 0,
        },
    },

    spaced: {
        '& > *': {
            marginLeft: theme.spacing(1),

            '&:first-child': {
                marginLeft: 0,
            },
        },

        '&.spaced-half > *': {
            marginLeft: theme.spacing(0.5),
        },
    },

    wrapped: {
        ...theme.mixins.flex.flexWrapWrap,
    },

    collapser: {
        ...theme.mixins.flex.grow(1),
        ...theme.mixins.flex.displayFlex,
        ...theme.mixins.flex.flexDirectionRow,
        ...theme.mixins.flex.justifyContentFlexEnd,

        cursor: 'pointer',
    },

    ellipsis: {
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
    },

    collapserIcon: {
        transform: 'rotate(0deg)',
        transitionProperty: 'transform',
        transitionDuration: '0.2s',
        transitionTimingFunction: 'ease',
    },

    collapserIconExpanded: {
        transform: 'rotate(180deg)',
    },

    rowTitle: {
        marginTop: theme.spacing(1),
    },
}));

export default function SiteCard(props) {
    const {
        meta: { site, status },
        bsGroup,
        timeRange,
        timeRangeSelection,
        onMouseEnter,
        onMouseLeave,
    } = props;

    const classes = useStyles();

    const { expandSite, collapseSite } = useSidebarStateActions();
    const [filterState] = useFilterState();
    const [, /* Not used */ pageFilterStateActions] = usePageFilterState();
    const [pageState] = usePageState();
    const isExpanded = useIsSiteExpanded(site);

    const siteUrlParams = getUrlParamFromEncodedState({
        [siteFilterId]: site.uuid,
        [timeRangeFilterId]: filterState?.encoded?.TimeRange,
    });
    const pageStateParams = getUrlParamFromState({ [selectedTimeId]: pageState?.values?.SelectedTime });

    const mouseEnterHandler = useCallback((e) => onMouseEnter(site, e), [onMouseEnter, site]);
    const mouseLeaveHandler = useCallback((e) => onMouseLeave(site, e), [onMouseLeave, site]);

    const siteUrl = `/${siteDetailPageId}?${siteUrlParams}&${pageStateParams}`;

    const toggleExpand = useCallback(
        () => (isExpanded ? collapseSite(site) : expandSite(site)),
        [collapseSite, expandSite, isExpanded, site]
    );

    return (
        <div
            className={clsx(classes.root)}
            onMouseEnter={mouseEnterHandler}
            onMouseLeave={mouseLeaveHandler}
            onClick={isExpanded ? noop : toggleExpand}
        >
            <div className={clsx(classes.row, classes.spaced)}>
                <StatusBlock color={status?.worst || 'gray'} />
                <DrillInLink to={siteUrl}>
                    <Typography variant="subtitle2">{site.name}</Typography>
                </DrillInLink>

                <Box
                    className={classes.collapser}
                    title={i18n._(t`Expands or collapses this site`)}
                    onClick={toggleExpand}
                >
                    <ExpandMore
                        className={clsx(classes.collapserIcon, isExpanded && classes.collapserIconExpanded)}
                        fontSize="small"
                    />
                </Box>
            </div>

            <div className={classes.row}>
                <Typography variant="caption" className={classes.ellipsis}>
                    {site.description}
                </Typography>
            </div>

            {isExpanded && <SiteStatusTrend site={site} bsGroup={bsGroup} timeRange={timeRange} />}

            {isExpanded && !isEmpty(site.tags) && (
                <div className={clsx(classes.row, classes.spaced, classes.wrapped, 'spaced-half')}>
                    {uniq(site.tags).map((tag) => (
                        <Box key={tag}>
                            <Chip label={tag} size="small" onClick={() => filterByTag(tag)} />
                        </Box>
                    ))}
                </div>
            )}

            {isExpanded && (
                <>
                    {!!site.notes && (
                        <>
                            <div className={classes.row}>
                                <Typography variant="subtitle2" className={classes.rowTitle}>
                                    <Trans>Notes</Trans>
                                </Typography>
                            </div>
                            <div className={classes.row}>
                                <Typography variant="caption">{site.notes}</Typography>
                            </div>
                        </>
                    )}

                    <ProblemsList
                        className={classes.rowTitle}
                        timeRange={timeRangeSelection}
                        site={site}
                        bsGroup={bsGroup}
                    />
                </>
            )}
        </div>
    );

    function filterByTag(tag) {
        pageFilterStateActions.setFilterState(siteGroupFilterId, {
            tags: [tag],
            tagOp: AND,
            sites: [],
        });
    }
}

const useStatusTrendStyles = makeStyles((theme) => ({
    root: {
        marginTop: theme.spacing(1),
        marginBottom: theme.spacing(1),
        height: theme.spacing(1),
    },

    statusContainer: {
        ...theme.mixins.flex.displayFlex,
        ...theme.mixins.flex.flexDirectionRow,
        height: '100%',
    },

    statusBlock: {
        ...theme.mixins.flex.grow(1),
        ...theme.mixins.flex.shrink(1),

        marginLeft: 1,
        opacity: 0.8,
        height: '100%',

        '&:first-child': {
            marginLeft: 0,
        },
    },
}));

function SiteStatusTrend(props) {
    const { timeRange, site, bsGroup } = props;
    const classes = useStatusTrendStyles();
    const colors = useStatusColors();

    const [data, isLoading] = useSiteStatusTrend({ timeRange, site, bsGroup });
    const worsts = bsGroup
        ? map(data?.[0]?.trends?.bsSiteCounts?.data, (trend) => getWorstStatusColor(trend?.value))
        : map(data?.[0]?.trends?.status?.data, (trend) => trend?.value?.worst || 'gray');

    return (
        <div className={classes.root}>
            <Fade in={isLoading}>
                <LinearProgress variant="indeterminate" />
            </Fade>
            <Fade in={!isLoading}>
                <div className={classes.statusContainer}>
                    {worsts.map((color, idx) => (
                        <div
                            key={`${color}-${idx}`}
                            className={classes.statusBlock}
                            style={{ backgroundColor: colors[color] }}
                        />
                    ))}
                </div>
            </Fade>
        </div>
    );
}
